<template>
  <div class="projects-page">
    <ProjectList
      :projects="projectsList"
      :saved-projects="savedProjects"
      :loading="isLoading"
      :remote="isRemote"
      @sync-selected="saveSelectedProjects"
      @set-delivered-projects="setDeliveredProjects"
      @delete-project="deleteProject"
      @send-project="sendProject"
    />
  </div>
</template>

<script>
import { productsObject } from "@/utils";

import ProjectList from "@/views/projects/components/ProjectList.vue";

const uuid = require("uuid/v4");

export default {
  name: "ProjectsPage",

  components: {
    ProjectList
  },

  data() {
    return {
      projects: [],
      patients: [],

      savedProjects: [],

      isRemote: this.$route.meta.remote || false,
      isLoading: false
    };
  },

  computed: {
    projectsList() {
      const projects = JSON.parse(JSON.stringify(this.projects));
      return projects
        .map(project => {
          // Find project patient
          let patient = this.patients.find(
            patientItem => project.patient_uuid == patientItem.uuid
          );

          if (!patient) {
            return project;
          }

          // Add fullname prop
          const lastname = patient.lastname || "";
          const firstname = patient.firstname || "";
          patient.fullname = `${lastname} ${firstname}`;
          patient.fullname = patient.fullname.trim() || "Non renseignée";
          // Add address prop
          const street = patient.address || "";
          const city = patient.city || "";
          const zipcode = patient.zipcode || "";
          patient.fullAddress = `${street} ${zipcode} ${city}`;
          patient.fullAddress = patient.fullAddress.trim() || "Non renseignée";
          // Add phone prop
          patient.phone = patient.phone || "Non renseigné";
          // Add center prop
          project.center = project.center || "Non renseigné";
          // Add product model prop translation
          project.product_model_trans =
            productsObject[project.product_model].name;
          // Add patient prop
          project.patient = patient;
          return project;
        })
        .sort(function(a, b) {
          a = new Date(a.created_at);
          b = new Date(b.created_at);
          return a > b ? -1 : a < b ? 1 : 0;
        });
    }
  },

  created() {
    this.init();
  },

  methods: {
    /**
     * Go to project page
     *
     * @param project Project clicked
     */
    goToProjectPage(project) {
      // Disable click when we are in remote mode
      if (this.remote) {
        return;
      }

      this.$router.push({
        name: "ProjectPage",
        params: { projectUuid: project.uuid }
      });
    },

    /**
     * Initialize
     * * Get projects and patients from IndexedDB
     */
    async init() {
      try {
        let projects,
          patients,
          savedProjects = [];

        this.isLoading = true;

        if (!this.isRemote) {
          projects = await this.$db.projects.toArray();
          patients = await this.$db.patients.toArray();
        }

        if (this.isRemote) {
          projects = await this.$store.dispatch("projects/getProjects");
          patients = await this.$store.dispatch("patients/getPatients");
          savedProjects = await this.$db.projects.toArray();
        }

        this.projects = projects;
        this.patients = patients;
        this.savedProjects = savedProjects;

        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;

        const user = await this.$db.user.toArray();
        const userId = user[0].user.email;

        if (
          userId == "vincent.mechin@albatros-france.fr" ||
          userId == "claire.dhuyvetter@albatros-france.fr"
        ) {
          alert(error);
        }
      }
    },

    /**
     * Save selected projects in IndexedDB
     * Remove deselected projects from IndexedDB
     * * Only remote mode
     *
     * @param selectedProjects Array of selected projects
     */
    async saveSelectedProjects(selectedProjects) {
      try {
        this.isLoading = true;

        const deselectedProjects = this.savedProjects.filter(
          item => selectedProjects.indexOf(item) == -1
        );

        for (let i = 0; i < deselectedProjects.length; i++) {
          const deselectedProject = deselectedProjects[i];
          await this.deleteProject(deselectedProject);
        }

        for (let i = 0; i < selectedProjects.length; i++) {
          // Go to next if no product
          if (!selectedProjects[i].product_uuid) continue;

          // Cancel if project already exist in IndexedDB
          const projectExist = await this.$db.projects.get(
            selectedProjects[i].uuid
          );
          if (projectExist) continue;

          const remoteProject = await this.$store.dispatch(
            "projects/getProject",
            selectedProjects[i].uuid
          );

          const remotePatient = await this.$store.dispatch(
            "patients/getPatient",
            remoteProject.patient_uuid
          );

          // Add server product version prop
          remoteProject.server_version = remoteProject.product_uuid;

          // Add local product version prop
          remoteProject.local_version = null;

          const getRemoteProductAction =
            productsObject[remoteProject.product_model].getRemoteProduct;

          const productTableName =
            productsObject[remoteProject.product_model].tableName;

          const remoteProduct = await this.$store.dispatch(
            getRemoteProductAction,
            remoteProject.product_uuid
          );

          const patientExist = await this.$db.patients.get(remotePatient.uuid);

          // Add patient to IndexedDB
          if (!patientExist) {
            await this.$db.patients.put(remotePatient);
          }

          // Add project to IndexedDB
          await this.$db.projects.put(remoteProject);

          // Add product to IndexedDB
          await this.$db[productTableName].put(remoteProduct);

          // Save project medias
          await this.saveMedias(remoteProject, remoteProject.medias);
        }

        const notification = {
          message: "Récupération réussie",
          type: "success"
        };
        this.$store.dispatch("notifications/add", notification);

        this.isLoading = false;
      } catch (error) {
        const notification = {
          message: error.message,
          type: "error"
        };
        this.$store.dispatch("notifications/add", notification);
      }
    },

    /**
     * Save project medias in IndexedDB
     * * Only remote mode
     *
     * @param project Project object
     */
    async saveMedias(project) {
      const medias = project.medias;
      if (!medias.length) return;

      for (let i = 0; i < medias.length; i++) {
        const media = medias[i];
        let remoteMedia, remoteMediaImage;
        let localMedia = {};

        try {
          remoteMedia = await this.$store.dispatch("medias/getMedia", {
            project_uuid: project.uuid,
            media_uuid: media.media_uuid
          });

          localMedia.uuid = remoteMedia.media_uuid;
          localMedia.project_uuid = project.uuid;
          localMedia.croquis = remoteMedia.croquis;

          if (remoteMedia.has_image) {
            remoteMediaImage = await this.$store.dispatch(
              "medias/getMediaImage",
              {
                project_uuid: project.uuid,
                media_uuid: media.media_uuid
              }
            );

            localMedia.image = remoteMediaImage.image;
          }

          await this.$db.medias.add(localMedia);
        } catch (error) {
          const notification = {
            message: `Le media ${
              remoteMedia.media_uuid
            } n'a pas pu être enregistré`,
            type: "error"
          };
          this.$store.dispatch("notifications/add", notification);
        }
      }
    },

    /**
     * Set project as delivered
     * * Only remote mode
     *
     * @param selectedProjects Array of selected projects
     */
    async setDeliveredProjects(selectedProjects) {
      try {
        this.isLoading = true;

        for (let i = 0; i < selectedProjects.length; i++) {
          const project = selectedProjects[i];

          await this.$store.dispatch(
            "projects/setDeliveredProject",
            project.uuid
          );
        }

        this.isLoading = false;

        const notification = {
          message: "Opération réussie",
          type: "success"
        };
        this.$store.dispatch("notifications/add", notification);
      } catch (error) {
        this.isLoading = false;
        const notification = {
          message: error.message,
          type: "error"
        };
        this.$store.dispatch("notifications/add", notification);
      }
    },

    /**
     * Delete project from IndexedDB
     * * Only local mode
     *
     * @param project Project object
     */
    async deleteProject(project) {
      // Check for new medias
      const localMedias = await this.$db.medias
        .where({ project_uuid: project.uuid })
        .toArray();

      // const projectMedias = project.medias || [];

      // Check for local product version
      // if (project.local_version || localMedias.length > projectMedias.length) {
      //   const hasConfirmed = confirm(
      //     "Version local detectée ! Voulez-vous vraiment supprimer ? Les données seront perdues !"
      //   );

      //   if (!hasConfirmed) return;
      // }

      const productTableName = productsObject[project.product_model].tableName;

      // Delete product
      await this.$db[productTableName].delete(project.product_uuid);

      // Delete medias
      for (let i = 0; i < localMedias.length; i++) {
        const media = localMedias[i];
        await this.$db.medias.delete(media.uuid);
      }

      // Delete project
      await this.$db.projects.delete(project.uuid);

      this.init();

      // const notification = {
      //   message: "Suppression réussie",
      //   type: "success"
      // };
      // this.$store.dispatch("notifications/add", notification);
    },

    /**
     * Send project to server
     * * Only local mode
     *
     * @param project Project object to send
     */
    async sendProject(project) {
      try {
        // Check if project exist
        const projectExist = await this.$store.dispatch(
          "projects/getProject",
          project.uuid
        );

        if (projectExist) {
          await this.updateProject(project);
        }

        if (!projectExist) {
          await this.createProject(project);
        }

        // Retrieves updated project from API
        const remoteProject = await this.$store.dispatch(
          "projects/getProject",
          project.uuid
        );

        // Add local properties
        remoteProject.local_version = null;
        remoteProject.server_version = remoteProject.product_uuid;
        remoteProject.transferable = false;

        await this.$db.projects.put(remoteProject);

        this.sendMedias(project);

        this.deleteProject(remoteProject);

        this.$notify({
          group: "main",
          type: "success",
          title: "Succès",
          text: "Fiche envoyée au serveur",
          duration: 5000
        });

        this.init();
      } catch (error) {
        const notification = {
          message: error.response.data,
          type: "error"
        };
        this.$store.dispatch("notifications/add", notification);
      }
    },

    /**
     * Send project medias to server
     * * Only local mode
     *
     * @param project Project object
     */
    async sendMedias(project) {
      const medias = await this.$db.medias
        .where({ project_uuid: project.uuid })
        .toArray();

      for (let i = 0; i < medias.length; i++) {
        const mediaExist = await this.$store.dispatch("medias/getMedia", {
          project_uuid: project.uuid,
          media_uuid: medias[i].uuid
        });

        const mediaTrashedExist = await this.$store.dispatch(
          "medias/getTrashedMedia",
          {
            project_uuid: project.uuid,
            media_uuid: medias[i].uuid
          }
        );

        if (mediaExist) {
          await this.$store.dispatch("medias/updateSketchMedia", {
            project_uuid: project.uuid,
            media_uuid: medias[i].uuid,
            payload: { croquis: medias[i].croquis }
          });
          continue;
        }

        // Medias was soft deleted
        if (mediaTrashedExist) {
          const localMedia = await this.$db.medias.get(medias[i].uuid);

          // Update uuid
          localMedia.uuid = uuid();

          // Add media with new uuid
          await this.$db.medias.add(localMedia);

          // Delete old media
          await this.$db.medias.delete(medias[i].uuid);

          await this.$store.dispatch("medias/createSketchMedia", {
            project_uuid: project.uuid,
            media_uuid: localMedia.uuid,
            payload: { croquis: localMedia.croquis }
          });
        } else {
          await this.$store.dispatch("medias/createSketchMedia", {
            project_uuid: project.uuid,
            media_uuid: medias[i].uuid,
            payload: { croquis: medias[i].croquis }
          });
        }

        if (medias[i].image) {
          let data = new FormData();

          data.append("image", medias[i].imageBlob, "image.png");

          await this.$store.dispatch("medias/createImageMedia", {
            project_uuid: project.uuid,
            media_uuid: medias[i].uuid,
            payload: data
          });
        }
      }
    },

    /**
     * Update remote project and create new product
     * * Only local mode
     *
     * @param project Project to update
     */
    async updateProject(project) {
      try {
        if (project.local_version) {
          const productTableName =
            productsObject[project.product_model].tableName;
          const createRemoteProductAction =
            productsObject[project.product_model].createRemoteProduct;

          let localProduct = await this.$db[productTableName].get(
            project.local_version
          );

          const loggedInUser = await this.$store.dispatch(
            "auth/getLoggedInUser"
          );

          // Add user_id prop
          localProduct.user_id = loggedInUser.id;

          // Create remote product
          await this.$store.dispatch(createRemoteProductAction, localProduct);
        }

        // Update remote project
        await this.$store.dispatch("projects/updateProject", {
          uuid: project.uuid,
          payload: project
        });
      } catch (error) {
        const errorCode = error.response.data.error;

        if (
          errorCode ==
          "ERR_APPLICATOR_WORKFLOW_CANNOT_EDIT_PROJECT_MOLDING_ARRIVAL_REACHED"
        ) {
          this.$swal({
            icon: "error",
            title: "Erreur !",
            text: error.response.data.message
          });
        }

        return Promise.reject(error);
      }
    },

    /**
     * Create remote project and create new product
     * * Only local mode
     *
     * @param project Project to create
     */
    async createProject(project) {
      try {
        // Get patient
        const localPatient = await this.$db.patients.get(project.patient_uuid);

        if (!localPatient) {
          throw new Error("FRONT_PATIENT_NOT_FOUND");
        }

        // Create remote patient
        await this.$store.dispatch("patients/createPatient", localPatient);

        // Create remote project
        await this.$store.dispatch("projects/createProject", project);

        const productTableName =
          productsObject[project.product_model].tableName;
        const createRemoteProductAction =
          productsObject[project.product_model].createRemoteProduct;

        let localProduct = await this.$db[productTableName].get(
          project.local_version
        );

        const loggedInUser = await this.$store.dispatch("auth/getLoggedInUser");

        // Add user_id prop
        localProduct.user_id = loggedInUser.id;

        // Create remote product
        await this.$store.dispatch(createRemoteProductAction, localProduct);
      } catch (err) {
        let error = err;

        const objectConstructor = {}.constructor;
        if (error.constructor === objectConstructor) {
          error = JSON.stringify(error);
        }

        const notification = {
          message: "Une erreur est survenue. Détail: " + error,
          type: "error"
        };
        this.$store.dispatch("notifications/add", notification);

        return Promise.reject(error);
      }
    }
  }
};
</script>
